import React, { useState } from 'react';
import { MenuItem } from '../types';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { formatEuro } from '../utils/format';
import { X, Plus, Minus, Check, Sparkles } from 'lucide-react';

interface ItemDetailModalProps {
  item: MenuItem | null;
  onClose: () => void;
}

export const ItemDetailModal: React.FC<ItemDetailModalProps> = ({ item, onClose }) => {
  const { addToCart } = useApp();
  const { lang, t } = useLanguage();

  const [quantity, setQuantity] = useState(1);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, any>>({});
  const [specialNotes, setSpecialNotes] = useState('');

  if (!item) return null;

  // Fully dynamic localized dish name & description
  const itemName = lang === 'en' ? item.name_en : lang === 'sv' ? item.name_sv : item.name_fi;
  const itemDesc = lang === 'en' ? item.description_en : lang === 'sv' ? item.description_sv : item.description_fi;

  const handleVariantSelect = (groupId: string, groupTitle: string, option: any) => {
    setSelectedVariants(prev => ({
      ...prev,
      [groupId]: {
        group_id: groupId,
        group_title: groupTitle,
        option_id: option.id,
        option_name: option.name[lang] || option.name.fi,
        price: option.price
      }
    }));
  };

  const variantExtraTotal = Object.values(selectedVariants).reduce(
    (sum: number, v: any) => sum + (v.price || 0),
    0
  );
  const unitPrice = item.price + variantExtraTotal;
  const totalPrice = unitPrice * quantity;

  const handleAdd = () => {
    addToCart(
      item,
      quantity,
      Object.values(selectedVariants),
      specialNotes.trim() || undefined
    );
    // Smoothly close modal without forcing cart drawer open so user can keep browsing dishes!
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 105 }}>
      <div
        className="modal-card"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '540px' }}
      >
        {/* Item Image */}
        <div style={{ position: 'relative', height: '230px', background: 'var(--bg-surface)' }}>
          <img
            src={item.image_url}
            alt={itemName}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <button
            className="modal-close-btn"
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(0,0,0,0.65)',
              color: '#fff'
            }}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          {/* Title & Price Header */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
              <h2 className="modal-title" style={{ fontSize: '1.35rem' }}>{itemName}</h2>
              <div
                style={{
                  fontSize: '1.3rem',
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 800,
                  color: 'var(--accent-copper)'
                }}
              >
                {formatEuro(unitPrice)}
              </div>
            </div>

            {/* Dietary Flags & ALV Badge */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
              {item.dietary_flags.map(flag => (
                <span
                  key={flag}
                  className={`dietary-badge ${flag.toLowerCase()}`}
                  style={{ width: 'auto', padding: '2px 8px', fontSize: '0.75rem' }}
                >
                  {flag === 'G' && (lang === 'en' ? 'Gluten-Free' : lang === 'sv' ? 'Glutenfri' : 'Gluteeniton')}
                  {flag === 'L' && (lang === 'en' ? 'Lactose-Free' : lang === 'sv' ? 'Laktosfri' : 'Laktoositon')}
                  {flag === 'VL' && (lang === 'en' ? 'Low Lactose' : lang === 'sv' ? 'Låglaktos' : 'Vähälaktoosinen')}
                  {flag === 'VEG' && (lang === 'en' ? 'Vegan' : lang === 'sv' ? 'Vegansk' : 'Vegaani')}
                  {flag === 'M' && (lang === 'en' ? 'Dairy-Free' : lang === 'sv' ? 'Mjölkfri' : 'Maidoton')}
                </span>
              ))}
              <span
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)',
                  marginLeft: 'auto'
                }}
              >
                {lang === 'en' ? `Incl. VAT ${item.vat_rate}%` : lang === 'sv' ? `Inkl. Moms ${item.vat_rate}%` : `Sis. ALV ${item.vat_rate}%`}
              </span>
            </div>
          </div>

          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.5' }}>
            {itemDesc}
          </p>

          {/* Variant Groups (Crust, extra toppings) */}
          {item.variants && item.variants.map(group => {
            const groupTitle = group.title[lang] || group.title.fi;
            return (
              <div key={group.id} className="form-group">
                <label className="form-label">
                  <span>{groupTitle}</span>
                  {group.required && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--accent-copper)' }}>
                      {lang === 'en' ? 'Required' : lang === 'sv' ? 'Obligatorisk' : 'Pakollinen'}
                    </span>
                  )}
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {group.options.map(opt => {
                    const optName = opt.name[lang] || opt.name.fi;
                    const isSelected = selectedVariants[group.id]?.option_id === opt.id;
                    return (
                      <div
                        key={opt.id}
                        onClick={() => handleVariantSelect(group.id, groupTitle, opt)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 14px',
                          borderRadius: 'var(--radius-md)',
                          background: isSelected ? 'var(--accent-copper-light)' : 'var(--bg-card)',
                          border: `1px solid ${isSelected ? 'var(--accent-copper)' : 'var(--border-subtle)'}`,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div
                            style={{
                              width: '18px',
                              height: '18px',
                              borderRadius: '50%',
                              border: `2px solid ${isSelected ? 'var(--accent-copper)' : 'var(--text-dim)'}`,
                              background: isSelected ? 'var(--accent-copper)' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {isSelected && <Check size={12} color="#fff" />}
                          </div>
                          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{optName}</span>
                        </div>
                        {opt.price > 0 && (
                          <span style={{ fontWeight: 700, color: 'var(--accent-amber)', fontSize: '0.88rem' }}>
                            +{formatEuro(opt.price)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Special notes */}
          <div className="form-group">
            <label className="form-label">{t('specialNotesLabel')}</label>
            <textarea
              className="form-input form-textarea"
              placeholder={t('specialNotesPlaceholder')}
              value={specialNotes}
              onChange={e => setSpecialNotes(e.target.value)}
            />
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              background: 'var(--bg-surface)',
              borderRadius: 'var(--radius-full)',
              padding: '4px'
            }}
          >
            <button
              onClick={() => setQuantity(q => Math.max(1, q - 1))}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'var(--bg-card)',
                color: 'var(--text-primary)'
              }}
            >
              <Minus size={16} />
            </button>
            <span style={{ minWidth: '36px', textAlign: 'center', fontWeight: 800 }}>
              {quantity}
            </span>
            <button
              onClick={() => setQuantity(q => q + 1)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'var(--bg-card)',
                color: 'var(--text-primary)'
              }}
            >
              <Plus size={16} />
            </button>
          </div>

          <button
            className="btn-primary"
            onClick={handleAdd}
            style={{ padding: '14px 24px', fontSize: '0.95rem' }}
          >
            <span>{t('addToOrder')}</span>
            <span>•</span>
            <span>{formatEuro(totalPrice)}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
